import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Skeleton,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { QuickSendContext } from "contexts/QuickSend";
import { QuickSendDrawerContext } from "contexts/QuickSendDrawer";
import { VaSettingsContext } from "contexts/VaSettings";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { OrganizationNiche } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";
import { routes } from "schemas/routes";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import StyledDialog from "components/Styled/Dialog";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import TemplateRubrics from "features/Influencer/HomePage/CreateTemplateDialog/TemplateRubrics";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";
import { containsMergeTagsTemplates } from "utils/string";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";
import { useBulkSend } from "utils/useBulkSend";
import { useGamification } from "utils/useGamification";

import TemplateBody from "../../SharedComponents/TemplateBody";
import { validateTemplateBody } from "../../SharedComponents/helpers";
import PreSaveDialog from "./components/PreSaveDialog";
import UnsavedChanges from "./components/UnsavedChanges";
import UserFirstTemplate from "./components/UserFirstTemplate";
import styles from "./styles";

interface EditTemplateDialogProps {
  isNew?: boolean;
  propsTemplateId?: string;
  propsHandleClose?: (e?: CustomEvent) => void;
  onSuccess?: () => void;
}

export default function EditTemplateDialog({
  isNew = false,
  propsTemplateId,
  propsHandleClose,
  onSuccess,
}: EditTemplateDialogProps) {
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  let { templateId } = useParams();
  if (propsTemplateId) {
    templateId = propsTemplateId;
    isNew = false;
  }
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useAuth();
  const { currentOrg, setProfile } = useContext(OrganizationUserContext);
  const { quickSendBrandId, setPreviewLoading, isEdited } = useContext(
    QuickSendDrawerContext,
  );
  const { fetchSettings, loadData } = useContext(VaSettingsContext);
  const { fetchNiches } = useBulkSend();
  const {
    templates,
    setTemplates,
    fetchOutreachTemplates,
    userHasNoTemplates,
    setDefaultTemplates,
    setUserHasNoTemplates,
    currentTemplateContentType,
    associatedNicheId,
    setAssociatedNicheId,
    askUserToEdit,
    setAskUserToEdit,
    onSaveAsNew,
  } = useContext(OutreachTemplatesContext);
  const {
    setTemplateForQuickSend,
    newlyAddedTemplateIndex,
    handleRefetchPreviews,
    setOutreachMessages,
  } = useContext(QuickSendContext);
  const { addTemplateTaskCompleted } = useGamification();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));

  const getDefaultContentType = () => {
    if (templates?.length === 0) return TemplateContentType.OUTREACH;
    return (
      currentTemplateContentType ||
      (newlyAddedTemplateIndex > 0
        ? TemplateContentType.FOLLOWUP
        : TemplateContentType.OUTREACH)
    );
  };
  const isUserFirstTemplate = templates?.filter((x) => x.id!)?.length === 0;

  // States
  const [editTemplate, setEditTemplate] = useState<Template>({
    name: isUserFirstTemplate ? "My Outreach" : "",
    subject: "",
    body: "",
    contentType: getDefaultContentType(),
    attachment: undefined,
  });
  const [followupTemplate, setFollowupTemplate] = useState<Template>({
    name: isUserFirstTemplate ? "My Follow-up" : "",
    subject: "",
    body: "",
    contentType: TemplateContentType.FOLLOWUP,
  });
  const [fetchTemplateLoading, setFetchTemplateLoading] = useState(
    Boolean(templateId),
  );
  const [createLoading, setCreateLoading] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [openIsDirtyWarning, setOpenIsDirtyWarning] = useState(false);
  const [openPresaveDialog, setOpenPresaveDialog] = useState<boolean>(false);
  const [openAskUserToEdit, setOpenAskUserToEdit] = useState<boolean>(false);
  const [openSaveAsNewDialog, setOpenSaveAsNewDialog] =
    useState<boolean>(false);
  const [saveAsNewName, setSaveAsNewName] = useState<string>("");
  const [openRubricsDialog, setOpenRubricsDialog] = useState<boolean>(false);
  const [originalNichesList, setOriginalNichesList] = useState<
    OrganizationNiche[]
  >([]);
  const hasMergeTag = containsMergeTagsTemplates(editTemplate.body || "");
  // Get a list of ALL OrganizationNiche inside originalNichesList that is not inside editTemplate?.niches
  const listOfUncheckNiches =
    editTemplate?.updateMultiNiches &&
    editTemplate?.contentType === TemplateContentType.OUTREACH
      ? originalNichesList.filter(
          (originalNiche) =>
            !editTemplate.niches?.some(
              (niche) => niche.id === originalNiche.id,
            ),
        )
      : [];

  const fetchTemplate = async () => {
    if (!currentOrg?.id || !templateId) {
      return;
    }
    setFetchTemplateLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/${templateId}`,
      );
      setEditTemplate(res.template);
      setOriginalNichesList(res.template.niches || []);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchTemplateLoading(false);
    }
  };

  const updateReviewedFirstTemplate = async () => {
    await fetcherAuth(
      getToken,
      `/api/organization/${currentOrg?.id}/templates/reviewed-first-template`,
      "POST",
      {},
    );
    setProfile((prev) => ({ ...prev, reviewedFirstTemplate: true }));
  };

  const createNewForNiche = async () => {
    if (!associatedNicheId) return;
    trackEvent("Create New Template for Niche", {
      "Template Id": templateId,
      "Niche Id": associatedNicheId,
    });
    setIsDirty(false);
    setCreateLoading(true);
    try {
      const { template } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates`,
        "POST",
        {},
        {
          ...editTemplate,
          nicheId: associatedNicheId,
          oldTemplateId: editTemplate.id,
        },
      );
      addTemplateTaskCompleted(template.contentType);
      setTemplates((prev) => [...prev, template]);
      await confirmedClose();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCreateLoading(false);
    }
  };

  const openSaveAsNewPrompt = () => {
    const { isValidTemplate, errorMessage } =
      validateTemplateBody(editTemplate);
    if (!isValidTemplate && errorMessage) {
      setAlert(errorMessage, "error");
      return;
    }
    setSaveAsNewName(`${editTemplate.name} - Copy`);
    setOpenSaveAsNewDialog(true);
  };

  const confirmSaveAsNew = async () => {
    if (!saveAsNewName.trim()) {
      setAlert("Please enter a template name", "error");
      return;
    }
    if (
      templates.find(
        (t: Template) =>
          t.name === saveAsNewName.trim() &&
          t.contentType === editTemplate.contentType,
      )
    ) {
      setAlert("You already have a template with the same name", "error");
      return;
    }
    if (!currentOrg?.id) return;
    setCreateLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates`,
        "POST",
        {},
        {
          ...editTemplate,
          id: undefined,
          name: saveAsNewName.trim(),
        },
      );
      const newTemplate = res.template;
      if (newTemplate.contentType === TemplateContentType.OUTREACH) {
        addTemplateTaskCompleted();
      }
      setTemplates((prev) => [...prev, newTemplate]);
      onSaveAsNew?.(newTemplate);
      setOpenSaveAsNewDialog(false);
      setAlert("Template saved as new", "success");
      await confirmedClose();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCreateOrSave = () => {
    const { isValidTemplate, errorMessage } =
      validateTemplateBody(editTemplate);
    if (!isValidTemplate && errorMessage) {
      setAlert(errorMessage, "error");
      return;
    }

    if (
      templates.find(
        (t: Template) =>
          t.name === editTemplate.name &&
          t.contentType === editTemplate.contentType &&
          t.id !== editTemplate.id,
      )
    ) {
      setAlert("You already have a template with the same name", "error");
      return;
    }

    if (askUserToEdit && !isDirty) {
      setOpenAskUserToEdit(true);
      return;
    }

    if (
      (isDirty &&
        (isEdited ||
          !hasMergeTag ||
          editTemplate?.attachedScheduledEmailsCount ||
          (editTemplate?.attachedNichesCount && associatedNicheId))) ||
      listOfUncheckNiches.length > 0
    ) {
      setOpenPresaveDialog(true);
    } else {
      saveTemplate();
    }
  };

  const saveFromOnboarding = async () => {
    try {
      const { outreach, followup } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/from-onboarding`,
        "POST",
        {},
        {
          outreach: editTemplate,
          followup: followupTemplate,
        },
      );
      await fetchOutreachTemplates();
      if (onSuccess) onSuccess();
      if (outreach && outreach?.id) {
        addTemplateTaskCompleted(TemplateContentType.OUTREACH);
      }
      if (followup && followup?.id) {
        addTemplateTaskCompleted(TemplateContentType.FOLLOWUP);
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCreateLoading(false);
    }
  };

  const saveTemplate = async (updatePendingEmails?: boolean) => {
    if (!currentOrg?.id) return;
    setIsDirty(false);
    setCreateLoading(true);
    if (isUserFirstTemplate) {
      await saveFromOnboarding();
      return;
    }
    const refreshNiches = editTemplate?.updateMultiNiches;
    try {
      const isUpdating = Boolean(
        templateId || (editTemplate?.id && editTemplate?.id > 0),
      );
      const res = await fetcherAuth(
        getToken,
        isUpdating
          ? `/api/organization/${currentOrg?.id}/templates/${
              templateId || editTemplate?.id
            }`
          : `/api/organization/${currentOrg?.id}/templates`,
        isUpdating ? "PUT" : "POST",
        {},
        {
          ...editTemplate,
          updatePendingEmails,
          validateBulkSendVariables: isPage(routes.onboardingVaSelfServe),
        },
      );
      setAlert("Template Saved", "success");
      setEditTemplate(res.template);
      if (userHasNoTemplates) {
        setDefaultTemplates([res.template]);
        setUserHasNoTemplates(false);
      }
      // User add new template from QuickSend mode
      if (newlyAddedTemplateIndex > -1) {
        setTemplateForQuickSend((prev) => {
          const copy = makeDeepCopy(prev);
          copy[newlyAddedTemplateIndex] = res.template;
          handleRefetchPreviews(copy);
          return copy;
        });
      }
      if (isEdited && updatePendingEmails) {
        handleUpdateQuickSend(res.template);
      }
      await confirmedClose(undefined, refreshNiches);
      await fetchOutreachTemplates();
      addTemplateTaskCompleted(res.template.contentType);
      if (onSuccess) onSuccess();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleUpdateQuickSend = (template: Template) => {
    trackEvent("Update QuickSend Template Button Clicked", {
      "Template Id": templateId,
    });
    setTemplateForQuickSend((prev) => {
      let foundTemplate = false;
      const copy = prev.map((t) => {
        if (t.id === template.id) {
          foundTemplate = true;
          return template;
        }
        return t;
      });
      if (foundTemplate) {
        setPreviewLoading(quickSendBrandId);
        setOutreachMessages([]);
        handleRefetchPreviews(copy);
      }
      return copy;
    });
  };

  const handleClose = (e?: CustomEvent) => {
    if (isDirty) {
      setOpenIsDirtyWarning(true);
    } else {
      confirmedClose(e);
    }
  };

  const confirmedClose = async (e?: CustomEvent, refreshNiches?: boolean) => {
    if (openPresaveDialog && isPage(routes.virtualAssistant)) {
      await fetchSettings();
      loadData();
    }
    if (
      refreshNiches ||
      (openPresaveDialog && isPage(routes.onboardingVaSelfServe))
    ) {
      await fetchNiches();
    }
    if (askUserToEdit && !isDirty && !openAskUserToEdit) {
      setOpenAskUserToEdit(true);
      return;
    }
    setIsDirty(false);
    setOpenIsDirtyWarning(false);
    setOpenPresaveDialog(false);
    setAssociatedNicheId(null);
    setAskUserToEdit(false);
    if (propsHandleClose) {
      propsHandleClose(e);
    } else {
      navigate(`/${routes.templates}`);
    }
  };

  useEffect(() => {
    if (currentOrg?.id) {
      fetchTemplate();
      updateReviewedFirstTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, currentOrg?.id]);

  const getDialogTitle = () => {
    if (isUserFirstTemplate) {
      return `Create your first pitch`;
    } else if (isNew) {
      return "Create a new template";
    } else {
      return "Modify your template";
    }
  };

  const dialogTitle = (
    <Box>
      <Box>{getDialogTitle()}</Box>
      <Box sx={{ display: { xs: "block", md: "none" }, mt: 0.5 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setOpenRubricsDialog(true)}
          sx={{ fontSize: "0.75rem" }}
        >
          Evaluate my template
        </Button>
      </Box>
    </Box>
  );

  return (
    <StyledDialog
      title={dialogTitle}
      open={true}
      maxWidth="lg"
      handleClose={handleClose}
      actionContainer={
        <>
          <Button
            disableElevation
            sx={styles.backButton}
            variant="contained"
            onClick={handleClose}
          >
            <Box
              component="i"
              className="fa-regular fa-circle-arrow-left"
              sx={{ mr: 1 }}
            />{" "}
            Back
          </Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            {onSaveAsNew && templateId && (
              <LoadingButton
                loading={createLoading}
                disableElevation
                variant="outlined"
                sx={{
                  px: 4,
                  py: 1,
                  borderColor: "primary.main",
                  color: "primary.main",
                }}
                onClick={openSaveAsNewPrompt}
              >
                Save as New Template
              </LoadingButton>
            )}
            <LoadingButton
              loading={createLoading}
              disableElevation
              variant="contained"
              sx={{ px: 4, py: 1 }}
              onClick={handleCreateOrSave}
            >
              Save Template
            </LoadingButton>
          </Box>
        </>
      }
    >
      {isUserFirstTemplate ? (
        <UserFirstTemplate
          createLoading={createLoading}
          editTemplate={editTemplate}
          setEditTemplate={setEditTemplate}
          followupTemplate={followupTemplate}
          setFollowupTemplate={setFollowupTemplate}
          setIsDirty={setIsDirty}
        />
      ) : (
        <>
          {fetchTemplateLoading ? (
            <Grid sx={{ width: { xs: 350, md: 800 } }}>
              <Skeleton animation="wave" width="100%" height="50px" />
              <Skeleton animation="wave" width="100%" height="50px" />
              <Skeleton
                animation="wave"
                width="100%"
                height="400px"
                sx={{ mt: -10, mb: -8 }}
              />
              <Skeleton animation="wave" width="100%" height="80px" />
            </Grid>
          ) : (
            <Grid container spacing={3} alignItems="flex-start">
              <Grid item xs={12} md={8}>
                <TemplateBody
                  editTemplate={editTemplate}
                  setEditTemplate={setEditTemplate}
                  setIsDirty={setIsDirty}
                  isNew={isNew}
                  allowChangeType={isNew}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md
                sx={{ display: { xs: "none", md: "block" } }}
              >
                <TemplateRubrics
                  template={editTemplate}
                  setTemplate={setEditTemplate}
                />
              </Grid>
            </Grid>
          )}
        </>
      )}

      <PreSaveDialog
        open={openPresaveDialog}
        handleClose={() => setOpenPresaveDialog(false)}
        template={editTemplate}
        handleConfirm={saveTemplate}
        loading={createLoading}
        createNewForNiche={createNewForNiche}
        listOfUncheckNiches={listOfUncheckNiches}
      />

      <UnsavedChanges
        isUserFirstTemplate={isUserFirstTemplate}
        confirmedClose={confirmedClose}
        setIsDirty={setIsDirty}
        setOpenIsDirtyWarning={setOpenIsDirtyWarning}
        openIsDirtyWarning={openIsDirtyWarning}
      />

      <ConfirmDialog
        header="Save template without editing?"
        subheader="You haven't made any changes to the AI-generated template. Would you like to continue editing or save it as is?"
        open={openAskUserToEdit}
        handleClose={() => setOpenAskUserToEdit(false)}
        handleCancel={() => {
          setOpenAskUserToEdit(false);
          trackEvent("Create Template Dialog Form - Continue Editing", {
            route: location.pathname,
          });
        }}
        cancelText="Continue Editing"
        confirmText="Save As Is"
        handleConfirm={() => {
          trackEvent("Create Template Dialog Form - Save As Is", {
            route: location.pathname,
          });
          setOpenAskUserToEdit(false);
          setAskUserToEdit(false);
          saveTemplate();
        }}
      />

      <Dialog
        open={openRubricsDialog}
        onClose={() => setOpenRubricsDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobileScreen}
      >
        <DialogTitle>Evaluate my template</DialogTitle>
        <DialogContent>
          <TemplateRubrics
            template={editTemplate}
            setTemplate={setEditTemplate}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenRubricsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openSaveAsNewDialog}
        onClose={() => setOpenSaveAsNewDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Save as New Template</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Template Name"
            fullWidth
            value={saveAsNewName}
            onChange={(e) => setSaveAsNewName(e.target.value)}
            sx={{ mt: 1 }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                confirmSaveAsNew();
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenSaveAsNewDialog(false)}>Cancel</Button>
          <LoadingButton
            loading={createLoading}
            variant="contained"
            onClick={confirmSaveAsNew}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </StyledDialog>
  );
}
